/**
 * DismissedRecsDrawer — recovery surface for operators who accidentally
 * dismissed a recommendation, or want to re-surface a snoozed one.
 *
 * Renders as a quiet "N dismissed · view" footer that opens a sheet listing
 * each currently-active dismissal with: rec text, channel, dismiss reason,
 * snooze-until date, and an "Un-dismiss" action that calls clearDismissal()
 * and re-surfaces the recommendation immediately.
 *
 * Cockpit-skinned (hairline borders, muted bg) — only used by the cockpit
 * dashboard, so no skin branching needed.
 */
import { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Undo2, RotateCcw } from "lucide-react";
import { useChannelEvents, getActiveDismissals, clearDismissal } from "@/data/channelRelationship";
import { toast } from "sonner";
import { format, isPast } from "date-fns";

const ACTOR = "Mike Johnson"; // matches CURRENT_REP in KoredorCommandDashboard

export function DismissedRecsDrawer() {
  // Subscribe to store changes so the count updates live.
  useChannelEvents();
  const [open, setOpen] = useState(false);
  const dismissed = getActiveDismissals();

  if (dismissed.length === 0) return null;

  const handleUndismiss = (key: string, recText: string) => {
    clearDismissal(key, ACTOR);
    toast.success("Recommendation re-surfaced", {
      description: recText.length > 80 ? recText.slice(0, 80) + "…" : recText,
    });
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="mt-2 w-full text-left text-[12px] text-muted-foreground hover:text-foreground transition-colors flex items-center justify-between px-1 py-1.5 group"
      >
        <span className="inline-flex items-center gap-1.5">
          <RotateCcw className="h-3 w-3 opacity-60 group-hover:opacity-100 transition-opacity" />
          {dismissed.length} dismissed · view
        </span>
        <span className="text-[11px] opacity-0 group-hover:opacity-60 transition-opacity">→</span>
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md">
          <SheetHeader>
            <SheetTitle className="text-[17px] font-semibold tracking-[-0.014em]">
              Dismissed recommendations
            </SheetTitle>
            <SheetDescription className="text-[13px]">
              {dismissed.length} currently snoozed. Re-surface one to bring it back into the Recommended list.
            </SheetDescription>
          </SheetHeader>

          <ul className="mt-6 space-y-3">
            {dismissed.map((d) => {
              const snoozeText = d.snoozeUntil
                ? isPast(new Date(d.snoozeUntil))
                  ? "Expired"
                  : `Until ${format(new Date(d.snoozeUntil), "MMM d")}`
                : "Indefinite";
              return (
                <li
                  key={d.id}
                  className="rounded-lg border border-border bg-card/40 px-4 py-3 space-y-2"
                >
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="text-[12px] font-medium text-foreground/80 truncate">
                      {d.channelName}
                    </span>
                    <span className="text-[11px] text-muted-foreground tabular-nums shrink-0">
                      {snoozeText}
                    </span>
                  </div>
                  {d.recommendationText && (
                    <p className="text-[13px] text-foreground leading-snug">
                      {d.recommendationText}
                    </p>
                  )}
                  {d.dismissReason && (
                    <p className="text-[12px] text-muted-foreground">
                      <span className="font-medium">Reason:</span> {d.dismissReason}
                    </p>
                  )}
                  <div className="flex items-center justify-between pt-1">
                    <span className="text-[11px] text-muted-foreground">
                      Dismissed {format(new Date(d.timestamp), "MMM d, h:mm a")} · {d.actor}
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-[12px] gap-1.5"
                      onClick={() => handleUndismiss(d.recommendationKey!, d.recommendationText || "")}
                    >
                      <Undo2 className="h-3 w-3" />
                      Un-dismiss
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        </SheetContent>
      </Sheet>
    </>
  );
}
